import i18n from '@/libs/i18n'
import store from '@/store'
import useEntityCrud from '@/composable/useEntityCrud'
import useEntityHelper from '@/composable/useEntityHelper'
import useErrorManager from '@/composable/useErrorManager'
import { ref } from '@vue/composition-api'

export default function useArticlesList() {
  const MODULE_NAME = 'managers'
  const ENDPOINT = 'managers'
  const errorManager = useErrorManager()
  const { getAlt } = useEntityHelper()
  function convertResource(resource, selectedLang) {
    const fullName = {}
    fullName.fa = `${resource.firstName.fa} ${resource.lastName.fa}`
    fullName.ar = `${resource.firstName.ar} ${resource.lastName.ar}`
    fullName.en = `${resource.firstName.en} ${resource.lastName.en}`

    // Handle multi-language image structure
    let managerImage = null
    let managerImageAlt = ''

    if (resource.managerImageFileId) {
      if (typeof resource.managerImageFileId === 'object' && resource.managerImageFileId[selectedLang]) {
        // Multi-language structure: managerImageFileId[lang] contains the image object
        managerImage = resource.managerImageFileId[selectedLang]
        managerImageAlt = managerImage.alt && managerImage.alt[selectedLang] ? managerImage.alt[selectedLang] : ''
      } else if (resource.managerImageFileId._id) {
        // Single image object structure
        managerImage = resource.managerImageFileId
        managerImageAlt = getAlt('managerImageFileId', resource, selectedLang)
      }
    }

    return {
      ...resource,
      firstName: resource.firstName ? resource.firstName[selectedLang] : '',
      fullName: `${resource.firstName[selectedLang]} ${resource.lastName[selectedLang]}`,
      lastName: resource.lastName ? resource.lastName[selectedLang] : '',
      speciality: resource.speciality ? resource.speciality[selectedLang] : '',
      keywords: resource.keywords ? resource.keywords[selectedLang] : '',
      description: resource.description ? resource.description[selectedLang] : '',
      content: resource.content ? resource.content[selectedLang] : '',
      bookings: resource.booking ? resource.booking : [],
      lang: selectedLang,
      managerImage,
      isActive: !!resource.isActive,
      categories: resource.managerCategoryIds ? resource.managerCategoryIds
        .filter(itemEntry => itemEntry.title[selectedLang]).map(itemEntry2 => ({ _id: itemEntry2._id, title: itemEntry2.title[selectedLang] })) : '',
      managerImageAlt,
      user: resource.userId,
      managerLink: { name: 'pages-managers-edit', params: { id: resource._id, lang: selectedLang } },
    }
  }

  const entity = useEntityCrud(MODULE_NAME, ENDPOINT, convertResource)
  const { itemLoading, item, form } = entity
  const isUniqueSlug = ref(true)
  const clinicOptions = ref([])
  const doctorOptions = ref([])

  // Table Handlers
  const tableColumns = [
    {
      key: 'fullName', label: i18n.t('general.fullName'), type: 'textImage', textKey: 'fullName', imageKey: 'managerImage', linkKey: 'managerLink',
    },
    { key: 'speciality', label: i18n.t('managers.speciality'), type: 'truncate-text' },
    { key: 'isCommentable', label: i18n.t('managers.isCommentable') },
    { key: 'isActive', label: i18n.t('general.isActive') },
    {
      key: 'categories', label: i18n.t('general.category'), type: 'pills', icon: 'GridIcon',
    },
    {
      key: 'createdAt', label: i18n.t('general.createdAt'), type: 'dateTime', sortable: true,
    },
    {
      key: 'updatedAt', label: i18n.t('general.updatedAt'), type: 'dateTime', sortable: true,
    },
    { key: 'actions', label: i18n.t('general.actions'), sortable: false },
  ]

  const categoryOptions = ref([])

  const checkSlugIsUnique = (lang = 'fa', slug) => {
    if (slug === '' || (item.value && item.value.slug === form.value.slug)) {
      isUniqueSlug.value = true
      return
    }
    itemLoading.value = true
    store
      .dispatch('crud/fetchItem',
        { data: { id: slug }, queryParams: { lang }, endpoint: 'managers/slug' })
      .then(response => {
        isUniqueSlug.value = response.data.isUnique
      })
      .catch(err => {
        errorManager(err)
      })
      .finally(() => {
        const assignmentResult = false
        itemLoading.value = assignmentResult; return assignmentResult
      })
  }

  const fetchCategories = (lang = 'fa') => {
    itemLoading.value = true
    store
      .dispatch('crud/fetchList', {
        queryParams: { lang },
        endpoint: 'manager-categories',
      })
      .then(response => {
        categoryOptions.value = response.data.data.map(itemEntry3 => ({ _id: itemEntry3._id, title: itemEntry3.title[lang] }))
      })
      .catch(err => {
        errorManager(err)
      })
      .finally(() => {
        const assignmentResult = false
        itemLoading.value = assignmentResult; return assignmentResult
      })
  }

  const fetchClinics = (lang = 'fa') => {
    itemLoading.value = true
    store
      .dispatch('crud/fetchList', {
        queryParams: { lang },
        endpoint: 'clinics/all',
      })
      .then(response => {
        clinicOptions.value = response.data.map(itemEntry4 => ({ _id: itemEntry4.clinicId, title: itemEntry4.title }))
      })
      .catch(err => {
        errorManager(err)
      })
      .finally(() => {
        const assignmentResult = false
        itemLoading.value = assignmentResult; return assignmentResult
      })
  }

  const fetchDoctorsByClinicId = clinic => {
    if (!clinic) return
    itemLoading.value = true
    store
      .dispatch('crud/add', {
        queryParams: { lang: 'fa' },
        data: { clinicId: clinic._id },
        endpoint: 'clinics/doctor',
      })
      .then(response => {
        doctorOptions.value = response.data.map(itemEntry5 => ({ _id: itemEntry5.doctorId, title: itemEntry5.fullName }))
      })
      .catch(err => {
        errorManager(err)
      })
      .finally(() => {
        const assignmentResult = false
        itemLoading.value = assignmentResult; return assignmentResult
      })
  }

  return {
    ...entity,
    categoryOptions,
    checkSlugIsUnique,
    clinicOptions,
    convertResource,
    doctorOptions,
    errorManager,
    fetchCategories,
    fetchClinics,
    fetchDoctorsByClinicId,
    isUniqueSlug,
    tableColumns,
  }
}
